import { Spin, Popover, Checkbox } from 'antd';
import LinkButton from 'components/shared/button/LinkButton';
import OverlayIcon from 'components/shared/overlay-icon/OverlayIcon';
import ConfigurableTable from 'components/shared/table/ConfigurableTable';
import {
  BOOLEAN_FALSE,
  BOOLEAN_TRUE,
  ColumnVisiblity,
  CUSTOMER_TYPE,
  ECOSYSTEM_OPPORTUNITY_COLUMNS_PAYER,
  ECOSYSTEM_OPPORTUNITY_COLUMNS_PARENT_PAYER,
  ECOSYSTEM_OPPORTUNITY_HELP_LINK,
  EcosystemOpportunityField,
  EcosystemOpportunityFieldMapping,
  EMPTY_ARRAY,
  NUMBER_ZERO,
  PAGINATION,
  SORT_BY,
  TABLE_NAME_ECOSYSTEM_OPPORTUNITY,
  QUERY_KEY,
  BenchMarkKPIColumns,
  COMPONENT_MAPPING,
  NUMBER_THIRTYFIVE,
  QUARTER_YEAR_DATE_FORMAT,
  YEAR_QUARTER_DATE_FORMAT,
} from 'constants/constants';
import { useEffect, useMemo, useState } from 'react';
import { Col, Row, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { excludeColumnFromExcel, formatDate } from 'utils/utils';
import {
  setEcosystemOpportunityBenefitType,
  setEcosystemOpportunityBookOfBusiness,
  setEcosystemOpportunityCompetitorBrand,
  setEcosystemOpportunityEcosystem,
  setEcosystemOpportunityIndication,
  setEcosystemOpportunityInitailState,
  setEcosystemOpportunityProduct,
  setEcosystemOpportunityRegion,
} from 'store/customer/customerViewSlice';
import {
  exportTableData,
  getEcoOpportunityeData,
} from 'store/customer/customer-view-actions';
import EcosystemOpportunityFilter from './EcosystemOpportunityFilter';
import SuggestedActions from './SuggestedActions';
import EcoOppBenchmarkTable from './EcoOppBenchmarkTable';
import classes from '../ecosystem-opportunity/ecosytemOpportunity.module.scss';
import OverlayTrigger from 'components/shared/tooltip/Tooltip';
import { useQuery } from '@tanstack/react-query';

const EcosystemOpportunity = () => {
  const selectedView = useSelector(
    (state) => state?.CustomerView?.selectedView
  );
  const isPayerView = selectedView === 'payer';

  const dispatch = useDispatch();

  const initialState = {
    values: EMPTY_ARRAY,
    offset: NUMBER_ZERO,
    totalSize: NUMBER_ZERO,
  };

  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(PAGINATION.defaultPageSize);
  const [sortBy, setSortBy] = useState({
    column: 'salesNumber',
    order: SORT_BY.descend,
  });

  const activeSectionTab = useSelector(
    (state) => state?.CustomerView?.activeSectionTab
  );

  const [popoverVisible, setPopoverVisible] = useState(BOOLEAN_FALSE);
  const ECO_OPP_COLUMNS = isPayerView
    ? ECOSYSTEM_OPPORTUNITY_COLUMNS_PAYER
    : ECOSYSTEM_OPPORTUNITY_COLUMNS_PARENT_PAYER;

  const isExporting = useSelector(
    (state) => state?.CustomerView?.exportEcosystemOpportunity
  );

  const getDefaultSelectedColumns = () => {
    return ECO_OPP_COLUMNS.filter(
      (col) =>
        col.columnVisiblity === ColumnVisiblity.Mandatory ||
        col.columnVisiblity === ColumnVisiblity.Default
    ).map((col) => col.key);
  };

  const [selectedColumns, setSelectedColumns] = useState(
    getDefaultSelectedColumns()
  );

  const payerTimeFrameDates = useSelector(
    (state) => state?.CustomerView?.payerTimeFrameDates
  );

  const { roleId } = useSelector((state) => state?.Auth?.userClaimObject);

  const selectedCustomer = useSelector(
    (state) => state?.CustomerView?.selectedCustomer
  );

  const columnRestriction = useSelector(
    (state) => state?.CustomerView?.columnsRestiction
  );

  const selectedEcoOppProduct = useSelector(
    (state) =>
      state.CustomerView?.ecosystemOpportunityFilters?.EcoOppProduct
        .selectedValue
  );
  const selectedEcoOppIndication = useSelector(
    (state) =>
      state.CustomerView?.ecosystemOpportunityFilters?.EcoOppIndication
        .selectedValue
  );
  const selectedEcoOppCompetitorProduct = useSelector(
    (state) =>
      state.CustomerView?.ecosystemOpportunityFilters?.EcoOppCompetitorProduct
        .selectedValue
  );
  const selectedEcoOppBookOfBusiness = useSelector(
    (state) =>
      state.CustomerView?.ecosystemOpportunityFilters?.EcoOppBookOfBusiness
        .selectedValue
  );
  const selectedEcoOppBenefitType = useSelector(
    (state) =>
      state.CustomerView?.ecosystemOpportunityFilters?.EcoOppBenefitType
        .selectedValue
  );
  const selectedEcoOppRegion = useSelector(
    (state) =>
      state.CustomerView?.ecosystemOpportunityFilters?.EcoOppRegion
        .selectedValue
  );
  const selectedEcoOppEcosystemName = useSelector(
    (state) =>
      state.CustomerView?.ecosystemOpportunityFilters?.EcoOppEcosystemName
        .selectedValue
  );

  // React Query to fetch PayerMix Data
  const { data: ecosystemData, isLoading } = useQuery({
    queryKey: [
      QUERY_KEY.EcosystemOpportunityTable,
      selectedCustomer?.value,
      selectedEcoOppProduct,
      selectedEcoOppIndication,
      selectedEcoOppCompetitorProduct,
      selectedEcoOppBookOfBusiness,
      selectedEcoOppBenefitType,
      selectedEcoOppRegion,
      selectedEcoOppEcosystemName,
      selectedColumns?.length,
      roleId,
    ],
    queryFn: async () => {
      const result = await getEcosystemOpportunityData();
      return result?.values?.length > 0 ? result : initialState;
    },
    enabled:
      activeSectionTab === COMPONENT_MAPPING.EcosystemOpportunity
        ? true
        : false,
  });

  const pagination = {
    current: pageIndex,
    total: ecosystemData?.totalSize,
    pageSize: pageSize,
    pageSizeOptions: PAGINATION.pageSizeOptions,
    showSizeChanger: BOOLEAN_TRUE,
    disabledPageSizehandler: BOOLEAN_TRUE,
  };

  const size = { x: 1100, y: 400 };

  const downloadData = async () => {
    const payload = {
      mdmId: selectedCustomer?.value,
      entityType: selectedCustomer?.flag,
      sortByColumn: selectedColumns?.includes(
        EcosystemOpportunityFieldMapping[sortBy.column]
      )
        ? EcosystemOpportunityFieldMapping[sortBy.column]
        : undefined,
      sortByDirection: selectedColumns?.includes(
        EcosystemOpportunityFieldMapping[sortBy.column]
      )
        ? sortBy.order
        : undefined,
      offset: NUMBER_ZERO,
      pageSize: pageSize,
      requestType: 'EcoOpp',
      selectedColumns: selectedColumns,
      product: selectedEcoOppProduct?.value,
      competitorProduct: selectedEcoOppCompetitorProduct?.value,
      indication: selectedEcoOppIndication?.value,
      ecosystemName: selectedEcoOppEcosystemName?.map((item) => item?.value),
      bookOfBusiness: selectedEcoOppBookOfBusiness?.value,
      benefitType: selectedEcoOppBenefitType?.value,
      region: selectedEcoOppRegion?.map((item) => item.value),
      roleId: roleId,
    };
    const customerType = CUSTOMER_TYPE.get(selectedCustomer?.flag);
    const customerTypeLabel =
      customerType === 'payer' ? 'Payer' : 'ParentPayer';
    const fileName = `${customerTypeLabel}-[${selectedCustomer?.label}-${selectedCustomer?.value}]-${TABLE_NAME_ECOSYSTEM_OPPORTUNITY}`;
    dispatch(
      exportTableData(TABLE_NAME_ECOSYSTEM_OPPORTUNITY, fileName, payload)
    );
  };

  const handlePopoverVisibleChange = (visible) => {
    setPopoverVisible(visible);
  };

  const handleCheckboxChange = (event) => {
    const { value, checked } = event.target;

    const orderedSelection = ECO_OPP_COLUMNS.map((col) => col.key).filter(
      (key) =>
        checked
          ? [...selectedColumns, value].includes(key)
          : selectedColumns.includes(key) && key !== value
    );

    setSelectedColumns(orderedSelection);
  };

  const getPopercolumns = (inputColumns, isAllColumns) => {
    if (isAllColumns) {
      return inputColumns;
    }
    return selectedColumns;
  };

  const filterColumns = (columns, selectedKeys) => {
    return columns.filter((column) => selectedKeys.includes(column.key));
  };

  const filteredColumns = filterColumns(ECO_OPP_COLUMNS, selectedColumns);

  const handleSorting = (column, order, pagination, filters) => {
    // Update pagination state - this handles both sorting and pagination changes
    if (pagination) {
      setPageIndex(pagination.current);

      // Handle page size changes
      if (pageSize !== pagination.pageSize) {
        setPageSize(pagination.pageSize);
      }
    }

    // Update sort state for display purposes - client-side sorting handles the actual sorting
    if (column && order) {
      setSortBy({ column, order: SORT_BY[order] });
    }

    // Security check: Verify user has access to sort this column
    if (column) {
      const restrictedColumns = excludeColumnFromExcel(columnRestriction);
      const fieldName = EcosystemOpportunityFieldMapping[column];

      if (restrictedColumns.includes(fieldName)) {
        console.warn(`Sorting denied on restricted column: ${column}`);
        return;
      }
    }
  };

  const getEcosystemOpportunityData = async () => {
    try {
      const payload = {
        mdmId: selectedCustomer?.value,
        entityType: selectedCustomer?.flag,
        // For client-side sorting, fetch all data without server-side sorting
        sortByColumn: undefined,
        sortByDirection: undefined,
        offset: NUMBER_ZERO,
        pageSize: NUMBER_THIRTYFIVE, // Fetch a reasonable number of records for client-side sorting
        requestType: 'EcoOpp',
        selectedColumns: [
          ...new Set([...selectedColumns, ...BenchMarkKPIColumns]),
        ],
        product: selectedEcoOppProduct?.value,
        competitorProduct: selectedEcoOppCompetitorProduct?.value,
        indication: selectedEcoOppIndication?.value,
        ecosystemName: selectedEcoOppEcosystemName?.map((item) => item.value),
        bookOfBusiness: selectedEcoOppBookOfBusiness?.value,
        benefitType: selectedEcoOppBenefitType?.value,
        region: selectedEcoOppRegion?.map((item) => item.value),
        roleId: roleId,
      };

      // Make API call
      const result = await getEcoOpportunityeData(payload);

      if (result?.values?.length > NUMBER_ZERO) {
        return {
          ...result,
          values: result.values,
          totalSize: result?.values?.length || 0,
        };
      } else {
        return { values: [], offset: 0, totalSize: 0 };
      }
    } catch (error) {
      console.log(error);
    }
  };

  const exportTooltip = (
    <Tooltip className='gne-tooltip'>Send to Excel</Tooltip>
  );

  useEffect(() => {
    setSelectedColumns(getDefaultSelectedColumns());
  }, [selectedCustomer?.value]);

  const ecosystemTooltip = (
    <Tooltip className='gne-tooltip'>
      <span>
        Sales Data (Plantrak and Imputed Sales) through{' '}
        {formatDate(payerTimeFrameDates?.salesDataAsOf)}
        <br />
        Market Share Data for{' '}
        {formatDate(
          payerTimeFrameDates?.marketShareLastCompletedQuarter,
          YEAR_QUARTER_DATE_FORMAT,
          QUARTER_YEAR_DATE_FORMAT
        )}{' '}
        <br />
        Policy Data (MMIT) through{' '}
        {formatDate(payerTimeFrameDates?.policyDataAsOf)}
      </span>
    </Tooltip>
  );

  const handleFilterChange = (value, field) => {
    if (field === EcosystemOpportunityField.Product) {
      if (!value) {
        dispatch(setEcosystemOpportunityIndication({ selectedValue: null }));
        dispatch(
          setEcosystemOpportunityCompetitorBrand({
            selectedValue: null,
          })
        );
      }
      dispatch(setEcosystemOpportunityProduct({ selectedValue: value }));
    }
    if (field === EcosystemOpportunityField.Indication) {
      if (!value) {
        dispatch(
          setEcosystemOpportunityCompetitorBrand({
            selectedValue: null,
          })
        );
      }
      dispatch(setEcosystemOpportunityIndication({ selectedValue: value }));
    }
    if (field === EcosystemOpportunityField.CompetitorBrand) {
      dispatch(
        setEcosystemOpportunityCompetitorBrand({ selectedValue: value })
      );
    }
    if (field === EcosystemOpportunityField.BookOfBusiness) {
      dispatch(setEcosystemOpportunityBookOfBusiness({ selectedValue: value }));
    }
    if (field === EcosystemOpportunityField.BenefitType) {
      dispatch(setEcosystemOpportunityBenefitType({ selectedValue: value }));
    }
    if (field === EcosystemOpportunityField.Region) {
      dispatch(setEcosystemOpportunityRegion({ selectedValue: value }));
    }
    if (field === EcosystemOpportunityField.Ecosystem) {
      dispatch(setEcosystemOpportunityEcosystem({ selectedValue: value }));
    }
    setPageIndex(1);
  };

  const resetFilters = () => {
    dispatch(setEcosystemOpportunityInitailState());
  };

  const isMandatoryFiltersSelected = useMemo(() => {
    const hasValidProduct = selectedEcoOppProduct !== null;
    const hasValidIndication = selectedEcoOppIndication !== null;
    const hasValidCompetitorProduct = selectedEcoOppCompetitorProduct !== null;
    const hasValidBookOfBusiness = selectedEcoOppBookOfBusiness !== null;
    const hasValidBenefitType = selectedEcoOppBenefitType !== null;

    return (
      hasValidProduct &&
      hasValidIndication &&
      hasValidCompetitorProduct &&
      hasValidBookOfBusiness &&
      hasValidBenefitType
    );
  }, [
    selectedEcoOppProduct,
    selectedEcoOppIndication,
    selectedEcoOppCompetitorProduct,
    selectedEcoOppBookOfBusiness,
    selectedEcoOppBenefitType,
  ]);

  return (
    <div id={'ecosystemOpportunity'}>
      <div className='pt-3'>
        <Row>
          <Col
            className='d-flex align-items-center'
            md={6}>
            <p className='title title__bold title__large mb-1'>
              Ecosystem Opportunity
              <OverlayTrigger overlay={ecosystemTooltip}>
                <i className='icon icon__info__gray ms-2'></i>
              </OverlayTrigger>
            </p>
          </Col>
          <Col
            className='d-flex justify-content-end align-items-center pe-0'
            md={6}>
            <LinkButton
              className={`d-flex title title__blue title__bold title__small me-2`}
              onClick={() => {
                window.open(ECOSYSTEM_OPPORTUNITY_HELP_LINK, '_blank');
              }}>
              <i className={`icon icon__help_blue`} />
              <span>Help</span>
            </LinkButton>
            <OverlayIcon
              tooltip={'Reset Filters'}
              onClick={resetFilters}
              className={`icon icon__reset-filters pointer align-center me-3`}
            />
            <OverlayTrigger overlay={exportTooltip}>
              <i
                onClick={downloadData}
                className={`icon icon__affiliations_export_excel1 align-center`}
              />
            </OverlayTrigger>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <div className='title title__x-small pt-0 title__grey d-flex '>
              <p className='title title__x-small title__italic title__grey'>
                This view will help identify high-priority ecosystems for the
                Payer's business and where to prioritize pull-through. Market
                Share data is based on claims | Data updated through{' '}
                {payerTimeFrameDates?.dataRefreshDate}
              </p>
            </div>
          </Col>
        </Row>
      </div>

      <EcosystemOpportunityFilter
        handleFilterChange={handleFilterChange}
        selectedEcoOppProduct={selectedEcoOppProduct}
        selectedEcoOppIndication={selectedEcoOppIndication}
        selectedEcoOppCompetitorProduct={selectedEcoOppCompetitorProduct}
        selectedEcoOppBookOfBusiness={selectedEcoOppBookOfBusiness}
        selectedEcoOppBenefitType={selectedEcoOppBenefitType}
        selectedEcoOppRegion={selectedEcoOppRegion}
        selectedEcoOppEcosystemName={selectedEcoOppEcosystemName}
      />
      <div className={classes.suggestedActionsContainer}>
        <Spin spinning={isLoading}>
          <SuggestedActions
            ecosystemOpportunityData={ecosystemData?.segment}
            isMandatoryFiltersSelected={isMandatoryFiltersSelected}
          />
        </Spin>
        <EcoOppBenchmarkTable
          isMandatoryFiltersSelected={isMandatoryFiltersSelected}
          benchmarkData={ecosystemData?.values?.[0]}
          isPayerView={isPayerView}
        />
        <div className={classes.opportunityContainer}>
          <Row className='pt-4 '>
            <Col
              className='d-flex align-items-center ps-0'
              md={6}>
              <div className='text text__bold mb-3 ms-2 me-0 title__large'>
                Ecosystem Opportunity Details
                <OverlayTrigger
                  overlay={
                    <Tooltip className='gne-tooltip'>
                      <span>
                        A comprehensive tabular view of key performance
                        indicators (KPIs), relative access positions, and
                        strategic recommendations for each ecosystem. This
                        tabular view can be exported to Excel for further
                        analysis.
                      </span>
                    </Tooltip>
                  }>
                  <i className='icon icon__info__gray ms-2'></i>
                </OverlayTrigger>
              </div>
            </Col>
            <Col
              className='d-flex justify-content-end align-items-center pe-0 mb-3'
              md={6}>
              <Popover
                content={
                  <div className={classes.acc_custom_popup}>
                    <ColumnSelectionPanel
                      classes={classes}
                      selectedColumns={getPopercolumns(selectedColumns)}
                      handleCheckboxChange={handleCheckboxChange}
                      columns={getPopercolumns(ECO_OPP_COLUMNS, true)}
                    />
                  </div>
                }
                trigger='click'
                placement='bottomRight'
                open={popoverVisible}
                onOpenChange={handlePopoverVisibleChange}
                getPopupContainer={(trigger) => trigger.parentElement}>
                <i
                  className={`icon icon__affiliations_standardTable_settings ms-3 me-3`}
                />
              </Popover>
            </Col>
          </Row>
          <Spin spinning={isLoading || isExporting}>
            <ConfigurableTable
              rows={ecosystemData?.values}
              columns={filteredColumns}
              expandRowByClick={BOOLEAN_FALSE}
              enableColumnSelection={BOOLEAN_FALSE}
              enableExport={BOOLEAN_FALSE}
              exportFileName={TABLE_NAME_ECOSYSTEM_OPPORTUNITY}
              tableName={TABLE_NAME_ECOSYSTEM_OPPORTUNITY}
              isGlobalExport={BOOLEAN_TRUE}
              pagination={pagination}
              basicSort={BOOLEAN_TRUE}
              onSort={handleSorting}
              dimentions={size}
            />
          </Spin>
        </div>
      </div>
    </div>
  );
};

const ColumnSelectionPanel = ({
  classes,
  selectedColumns,
  handleCheckboxChange,
  columns,
}) => {
  return (
    <Checkbox.Group
      defaultValue={selectedColumns}
      className={classes.checkboxGroup}
      key={Math.random()}>
      {columns.map((column) => {
        let label = column.label ? column.label : column.title;
        return (
          <div
            className={classes.checkboxWrapper}
            key={column.id}>
            <Checkbox
              className={`${classes.checkbox}`}
              key={column.key}
              value={column.key}
              disabled={column.columnVisiblity === ColumnVisiblity.Mandatory}
              onChange={handleCheckboxChange}>
              <div className={`${classes['truncate-text']}`}>{label}</div>
            </Checkbox>
          </div>
        );
      })}
    </Checkbox.Group>
  );
};

export default EcosystemOpportunity;
